import {
	InterestedParty,
	LiveInformationProvider,
	SuccessfulCacheItem
} from "client_legacy/Workspace/LiveInformation/liveInformationProvider";
import { Guid } from "client_legacy/ndaw/guid";
import {
	ZenyaDocInterests,
	ZenyaDocLiveInformationResponseModel,
	ZenyaDocLiveInformationRequestModel,
	getLiveInformation,
	ZenyaDocRecentItemsResponseModel,
	ZenyaDocFavoritesResponseModel,
	listPortals,
	ZenyaDocPortal
} from "client/Api/Workspace/LiveInformation/ZenyaDoc/zenyaDocLiveInformation";
import { ensureNotNil } from "client/Misc/utils";
import { NormalizedMap } from "client_legacy/ndaw/normalizedMap";

type ZenyaDocLiveInformationProviderOptions = {
	intervalInSeconds: number;
};

type ZenyaDocResourceId = {
	connectionId: number;
};

interface InterestedZenyaDocParty extends InterestedParty<Guid, ZenyaDocResourceId> {
	updateRecentItems?(response: ZenyaDocRecentItemsResponseModel): void;
	updateFavorites?(response: ZenyaDocFavoritesResponseModel): void;
}

class ZenyaDocLiveInformationProvider extends LiveInformationProvider<
	ZenyaDocLiveInformationProviderOptions,
	Guid,
	ZenyaDocResourceId,
	ZenyaDocInterests,
	InterestedZenyaDocParty,
	ZenyaDocLiveInformationResponseModel
> {

	private readonly _portalsCache: Map<number, Promise<readonly ZenyaDocPortal[]>>;

	constructor(options: ZenyaDocLiveInformationProviderOptions) {
		super(options);

		this._portalsCache = new Map<number, Promise<readonly ZenyaDocPortal[]>>();
	}

	public listPortals(connectionId: number): Promise<readonly ZenyaDocPortal[]> {
		if (!this._portalsCache.has(connectionId)) {
			const promise = listPortals(connectionId);
			this._portalsCache.set(connectionId, promise);
		}

		return this._portalsCache.get(connectionId)!;
	}

	protected async getDataFromBackend(
		entries: ReadonlyArray<[ZenyaDocResourceId, ZenyaDocInterests]>
	): Promise<ReadonlyArray<[ZenyaDocResourceId, ZenyaDocLiveInformationResponseModel]>> {
		const requests: ZenyaDocLiveInformationRequestModel[] = entries.map(entry => this.convertResourceIdToRequest(entry[0], entry[1]));
		const responses: ReadonlyArray<ZenyaDocLiveInformationResponseModel> = await getLiveInformation(requests);

		return responses.map(response => [this.convertResponseToResourceId(response), response]);
	}

	protected updateCacheItem(
		cacheItem: SuccessfulCacheItem<ZenyaDocInterests, ZenyaDocLiveInformationResponseModel>,
		response: [ZenyaDocInterests, ZenyaDocLiveInformationResponseModel]
	): void {
		const data: ZenyaDocLiveInformationResponseModel = response[1];

		if (data.interests.recentItems) {
			cacheItem.retrievedInterests.recentItems = true;
			cacheItem.data.data.recentItems = data.data.recentItems;
		}

		if (data.interests.favorites) {
			cacheItem.retrievedInterests.favorites = true;
			cacheItem.data.data.favorites = data.data.favorites;
		}
	}

	protected informParty(
		party: InterestedZenyaDocParty,
		cacheItem: [ZenyaDocInterests, ZenyaDocLiveInformationResponseModel]
	): void {
		const [retrievedInterests, response] = cacheItem;
		const interestedIn: ZenyaDocInterests = this.getInterestsForEnsuredParty(party);

		const {
			recentItems,
			favorites
		} = response.data;

		if (interestedIn.recentItems && retrievedInterests.recentItems && party.updateRecentItems)
		{
			party.updateRecentItems(
				ensureNotNil(
					recentItems
				)
			);
		}

		if (interestedIn.favorites && retrievedInterests.favorites && party.updateFavorites)
		{
			party.updateFavorites(
				ensureNotNil(
					favorites
				)
			);
		}
	}

	protected override createResourceMap<TValue>(): Map<ZenyaDocResourceId, TValue> {
		return new NormalizedMap<ZenyaDocResourceId, TValue>(
			(key) => `${key.connectionId}`
		);
	}

	private convertResourceIdToRequest(
		resourceId: ZenyaDocResourceId,
		interestedIn: ZenyaDocInterests
	): ZenyaDocLiveInformationRequestModel {

		return {
			connectionId: resourceId.connectionId,
			interestedIn: interestedIn
		};
	}

	private convertResponseToResourceId(
		response: ZenyaDocLiveInformationResponseModel
	): ZenyaDocResourceId {
		return {
			connectionId: response.connectionId
		};
	}
}

export {
	type InterestedZenyaDocParty,
	type ZenyaDocLiveInformationProviderOptions,
	ZenyaDocLiveInformationProvider,
	type ZenyaDocResourceId
};